/**
 * debounced calls that go TIC -> AI Engine to avoid any infinite loops causing real damage.
 */

import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AiLog } from '@timelyapp/integration-cloud-api-spec';
import { RATE_LIMITS } from '@timelyapp/integration-cloud-api-spec';
import { transformLogsToStatuses } from '@timelyapp/integration-cloud-sdk/utils/ai_predictions/ai_prediction_logs.transformers';
import { debounce, throttle } from 'lodash-es';
import { DateTime } from 'luxon';
import { safelyDelayExecution } from 'redux/reusable/util.safelyDelayExecution';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import { getDerivedDatesInfo } from 'redux/selectors/calendar';
import { getUserId } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { fromISO } from 'utils/luxon-helpers';
import type {
  RefreshAiPredictionsInDateRangeApiArg,
  RetrainAiModelApiArg,
} from '~/redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~/redux/modules/tic/api-slice/tic.apiSlice';
import { showToast } from '~redux/modules/toasts.actions';
import { getRecentProjectsIdsForHourDraft } from '~redux/selectors/hours.draft.project';
import { LogLevel } from '~utils/debug';
import { getIsViewingTimesheet } from '../../timesheet_proposals/redux/tic.timesheetProposals.slice';
import {
  getAiDerivedStatus,
  getAiPredictionStatusForDateQuery,
  getAiPredictionStatusForDateRangeRTKQueryData,
  getIsAiCurrentlyInferring,
  getIsAiCurrentlyTraining,
  getTicAiPreferences,
} from './predictions.selectors';
import { type SetAiStatusOptions, setAiStatus } from './predictions.sideEffects.actions';

/**
 * Determine delays based on RATE_LIMITS in `models.api.contract.metadata.ts`
 * @see RATE_LIMITS
 */
const INFERENCE_DELAY_SEC = 60 / RATE_LIMITS.ai_inference.reqPerMin; // 15s
const MODEL_TRAINING_DELAY_SEC = 60 / RATE_LIMITS.ai_model_training.reqPerMin; // 10s

type ControlOptions = {
  /** How should we fire the request? `throttle` (Run at most once every X), or `debounce` (Run Xmins after it was last called) */
  delayStrategy: 'throttle' | 'debounce' | 'instant';
  /* Specifies the delay in minutes _before_ the action is executed with `delayStrategy`  */
  delayInMinutes?: number;
};

/**
 * Individual AI logs returned from e.g, requesting inference or model re-training
 * will often replace an existing status; To avoid server round-trip, we'll
 * do this locally.
 *
 * @warning Beware, this path WILL NOT lead to AI checks running, so use only for non-side effects
 * @todo Send status to server to ensure consistency after reload.
 */
export const injectAiLogToStatusForDateRange = createAsyncThunk<
  void,
  AiLog,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/injectAiLogToStatusForDateRange', async (logEntry, { dispatch, getState }) => {
  const state = getState() as IState;
  const query = getAiPredictionStatusForDateQuery(state);
  const aiLogs = getAiPredictionStatusForDateRangeRTKQueryData(state);
  const logs = [...(aiLogs?.status || []), logEntry];

  // New status is determine and overwrites existing status
  const newStatus = transformLogsToStatuses(logs);
  dispatch(
    ticApi.util.updateQueryData('getAiPredictionStatusForDateRange', query, draft => {
      return Object.assign(draft, newStatus);
    })
  );
});

/**
 * ==============================================================
 * Inference/Prediction
 * ==============================================================
 */

/** Fire inference + toast/status */
const _refreshAiPredictionsInDateRange = createAsyncThunk<
  void,
  RefreshAiPredictionsInDateRangeApiArg & ControlOptions,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/_refreshAiPredictionsInDateRange', async (args, { dispatch }) => {
  // Set the requested event when the actual XHR request is made
  dispatch(
    setAiStatus({
      event: 'inference_requested',
      day: args.body.from_date,
      message: 'AI inference requested',
      debugData: args,
    })
  );

  dispatch(ticApi.endpoints.refreshAiPredictionsInDateRange.initiate(args, {}));
});

/** Debounce -> toast & run inference */
const _internal_debounce_refreshAiPredictionsInDateRange = debounce(
  (
    { dispatch }: { dispatch: ThunkDispatch<IState, any, Action>; getState: () => IState },
    args: RefreshAiPredictionsInDateRangeApiArg & ControlOptions
  ) => dispatch(_refreshAiPredictionsInDateRange(args)),
  INFERENCE_DELAY_SEC * 1_000
);

/** Throttle -> toast & run inference */
const _internal_throttle_refreshAiPredictionsInDateRange = throttle(
  (
    { dispatch }: { dispatch: ThunkDispatch<IState, any, Action>; getState: () => IState },
    args: RefreshAiPredictionsInDateRangeApiArg & ControlOptions
  ) => dispatch(_refreshAiPredictionsInDateRange(args)),
  INFERENCE_DELAY_SEC * 1_000
);

/**
 * Debounce, throttle, or instantly request AI Engine inference run.
 * Can be combined with `delayInMinutes` to delay the (debounced/throttled/instant) request.
 */
export const safelyDelayRefreshAiPredictionsInDateRange = createAsyncThunk<
  void,
  Partial<RefreshAiPredictionsInDateRangeApiArg> & ControlOptions,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/safelyDelayRefreshAiPredictionsInDateRange', async (_args, thunkAPI) => {
  const { getState, dispatch } = thunkAPI;
  const state = getState();
  const isViewingTimesheet = getIsViewingTimesheet(state);
  const { delayStrategy = 'debounce' as const, delayInMinutes = 0, ...restArgs } = _args;

  // DO NOT bail early if `inference_requested` is already running, just debug FYI
  if (getIsAiCurrentlyInferring(state) && !isViewingTimesheet) {
    dispatch(
      showToast({
        title: 'Timesheet Assistant',
        message: 'Already generating suggestions, but allowing request to continue',
        logLevel: LogLevel.Debug,
      })
    );
  }

  // Get required params from state if not provided
  const timely_account_id = getCurrentAccountId(state);
  const timely_user_id = getUserId(state);
  const dateMeta = getDerivedDatesInfo(state);
  const recentProjectIds = getRecentProjectsIdsForHourDraft(state);

  // Indicates to AI inference when the last notable updated happened,
  // via `ai_preferences.updated_at` OR when AI last inferred current date.
  const determineConsequentialChangeAt = () => {
    const { updated_at } = getTicAiPreferences(state);
    const aiStatus = getAiDerivedStatus(state);

    const aiPrefsUpdatedAt = updated_at ? fromISO(updated_at).toUnixInteger() : 0;
    const dateLastInferedAt = aiStatus?.dateLastInferedAt
      ? fromISO(aiStatus.dateLastInferedAt).toUnixInteger()
      : 0;

    const lastConsequentialTimestamp = Math.max(aiPrefsUpdatedAt, dateLastInferedAt);
    return lastConsequentialTimestamp
      ? (DateTime.fromSeconds(lastConsequentialTimestamp).toISO() as string)
      : undefined;
  };

  const payload = {
    ...restArgs,
    delayStrategy,
    delayInMinutes,
    body: {
      ...restArgs?.body,
      timely_account_id: restArgs?.body?.timely_account_id || timely_account_id,
      timely_user_id: restArgs?.body?.timely_user_id || timely_user_id,
      from_date: restArgs?.body?.from_date || dateMeta.since,
      to_date: restArgs?.body?.to_date || dateMeta.upto,
      consequential_change_at: determineConsequentialChangeAt(),
      // Dev envs fake suggestions using these project ids if supplied.
      timely_recent_project_ids: recentProjectIds.length ? recentProjectIds.slice(0, 3) : undefined,
    },
  } satisfies RefreshAiPredictionsInDateRangeApiArg & ControlOptions;

  // Calculate total delay in seconds (delayStrategy + delayInMinutes)
  const rateDelaySeconds = delayStrategy !== 'instant' ? INFERENCE_DELAY_SEC : 0;
  const pauseFirstDelaySeconds = delayInMinutes * 60;
  const totalDelaySeconds = rateDelaySeconds + pauseFirstDelaySeconds;

  // Set appropriate toast message & debug messages, assuming instant request
  const aiStatusPayload: SetAiStatusOptions = {
    day: payload.body.from_date,
    event: 'inference_requested',
    message: `Generating suggestions for you`,
    toast: { title: 'Timesheet Assistant', type: 'info', logLevel: LogLevel.Debug },
    debugData: payload,
  };

  // If we have delay, request won't fire until then; tweak state to be pending...
  if (totalDelaySeconds > 0) {
    aiStatusPayload.event = 'inference_pending';
    // Super temporary state, which doesn't recover properly if we fail, so..
    aiStatusPayload.saveToServer = false;
    aiStatusPayload.message = `About to generate suggestions (${delayStrategy}, up to ${totalDelaySeconds}sec delay)`;
    // aiStatusPayload.updated_at = DateTime.now().plus({ seconds: totalDelaySeconds }).toISO();
    aiStatusPayload.toast = {
      ...aiStatusPayload.toast,
      dismissAfter: totalDelaySeconds * 1_000,
    };
  }

  // Debug toast for everything that isn't an instant request
  if (delayStrategy !== 'instant') {
    dispatch(setAiStatus(aiStatusPayload));
  }

  // Additional delay _before_ calling debounce/throttle/instant XHR operation:
  if (delayInMinutes) {
    await safelyDelayExecution({ id: 'inference_requested', delayInMinutes });
  }

  // Execute the request based on the delay strategy
  if (delayStrategy === 'debounce') {
    _internal_debounce_refreshAiPredictionsInDateRange(thunkAPI, payload);
  } else if (delayStrategy === 'throttle') {
    _internal_throttle_refreshAiPredictionsInDateRange(thunkAPI, payload);
  } else if (delayStrategy === 'instant') {
    dispatch(_refreshAiPredictionsInDateRange(payload));
  }
});

/**
 * ==============================================================
 * Model Training
 * ==============================================================
 */

/** Fire retraining + toast/status */
const _retrainAiModel = createAsyncThunk<
  void,
  RetrainAiModelApiArg & ControlOptions,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/_retrainAiModel', async (args, { dispatch }) => {
  // Set the requested event when the actual XHR request is made
  dispatch(
    setAiStatus({
      event: 'training_requested',
      day: null,
      message: 'AI model training requested',
      debugData: args,
    })
  );

  dispatch(ticApi.endpoints.retrainAiModel.initiate(args, {}));
});

/** Throttle -> toast & retrain model */
const _internal_throttle_retrainAiModel = throttle(
  (
    { dispatch }: { dispatch: ThunkDispatch<IState, any, Action>; getState: () => IState },
    args: RetrainAiModelApiArg & ControlOptions
  ) => dispatch(_retrainAiModel(args)),
  MODEL_TRAINING_DELAY_SEC * 1_000
);

/** Debounce -> toast & retrain model */
const _internal_debounce_retrainAiModel = debounce(
  (
    { dispatch }: { dispatch: ThunkDispatch<IState, any, Action>; getState: () => IState },
    args: RetrainAiModelApiArg & ControlOptions
  ) => dispatch(_retrainAiModel(args)),
  MODEL_TRAINING_DELAY_SEC * 1_000
);

/**
 * Trigger re-training of the AI model
 */
export const safelyDelayRetrainAiModel = createAsyncThunk<
  void,
  (Partial<RetrainAiModelApiArg> & ControlOptions) | undefined,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/safelyDelayRetrainAiModel', async (_args, thunkAPI): Promise<void> => {
  const { getState, dispatch } = thunkAPI;
  const state = getState();
  const isViewingTimesheet = getIsViewingTimesheet(state);

  // Bail early if `training_requested` is already running
  if (getIsAiCurrentlyTraining(state) && !isViewingTimesheet) {
    dispatch(
      showToast({
        title: 'Timesheet Assistant',
        message: 'Already learning from your time tracking, skipping request',
        logLevel: LogLevel.Debug,
      })
    );
    return;
  }

  const args = { delayInMinutes: 0, delayStrategy: 'debounce' as const, ..._args };
  const { delayInMinutes, delayStrategy } = args || {};

  // In case of no params, inject require params to query
  const timely_account_id = getCurrentAccountId(state);
  const timely_user_id = getUserId(state);

  const payload = {
    ...args,
    body: {
      ...args?.body,
      timely_account_id: args?.body?.timely_account_id || timely_account_id,
      timely_user_id: args?.body?.timely_user_id || timely_user_id,
    },
  } satisfies RetrainAiModelApiArg & ControlOptions;

  // Calculate total delay in seconds (delayStrategy + delayInMinutes)
  const rateDelaySeconds = delayStrategy !== 'instant' ? MODEL_TRAINING_DELAY_SEC : 0;
  const pauseFirstDelaySeconds = delayInMinutes * 60;
  const totalDelaySeconds = rateDelaySeconds + pauseFirstDelaySeconds;

  // Set appropriate toast message & debug messages:
  const aiStatusPayload: SetAiStatusOptions = {
    day: null,
    event: 'training_requested',
    message: 'Learning from your recent time tracking',
    toast: { title: 'Timesheet Assistant', type: 'info', logLevel: LogLevel.Debug },
    debugData: payload,
  };

  // If we have delay, request won't fire until then; tweak state to be pending...
  if (totalDelaySeconds > 0) {
    aiStatusPayload.event = 'training_pending';
    // Super temporary state, which doesn't recover properly if we fail, so..
    aiStatusPayload.saveToServer = false;
    aiStatusPayload.message = `About to learn from recent time tracking (${delayStrategy}, up to ${totalDelaySeconds}sec delay)`;
    // aiStatusPayload.updated_at = DateTime.now().plus({ seconds: totalDelaySeconds }).toISO();
    aiStatusPayload.toast = {
      ...aiStatusPayload.toast,
      dismissAfter: totalDelaySeconds * 1_000,
    };
  }

  // Debug toast for everything that isn't an instant request
  if (delayStrategy !== 'instant') {
    dispatch(setAiStatus(aiStatusPayload));
  }

  // Additional delay _before_ calling debounce/throttle/instant XHR operation:
  if (delayInMinutes) {
    await safelyDelayExecution({ id: 'training_requested', delayInMinutes });
  }

  if (delayStrategy === 'debounce') {
    _internal_debounce_retrainAiModel(thunkAPI, payload);
  } else if (delayStrategy === 'throttle') {
    _internal_throttle_retrainAiModel(thunkAPI, payload);
  } else if (delayStrategy === 'instant') {
    dispatch(_retrainAiModel(payload));
  }
});
